/**
 * Visual Feedback Provider Component
 * Coordinates all visual feedback systems with context management
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";

import { FourStateVisualFeedbackSystem } from "./FourStateVisualFeedbackSystem";
import { HardwareAcceleratedAnimationFramework } from "./HardwareAcceleratedAnimationFramework";
import { ComponentSpecificFeedbackPatterns } from "./ComponentSpecificFeedbackPatterns";
import { PerformanceOptimizedAnimationSystem } from "./PerformanceOptimizedAnimationSystem";
import { ErrorStateVisualizationSystem } from "./ErrorStateVisualizationSystem";
import { SpatialIndexingIntegration } from "./SpatialIndexingIntegration";
import { AccessibilityIntegration } from "./AccessibilityIntegration";
import { PerformanceValidator } from "./PerformanceValidator";

import {
  VisualFeedbackState,
  VisualFeedbackContext,
  VisualFeedbackConfig,
  AnimationPerformanceMetrics,
  ErrorType,
  VisualFeedbackSystem,
  MultiViewportSync,
  SpatialIndexingOptimization,
} from "./types";
import type { PerformanceConfig } from "@/types/canvas-legacy-types";

import { ComponentType, Position } from "@/types";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useResponsiveStore } from "@/stores/editor/responsive-store";

// Provider context interface
interface VisualFeedbackContextValue {
  // Core systems
  feedbackSystem: FourStateVisualFeedbackSystem;
  animationFramework: HardwareAcceleratedAnimationFramework;
  componentPatterns: ComponentSpecificFeedbackPatterns;
  performanceSystem: PerformanceOptimizedAnimationSystem;
  errorSystem: ErrorStateVisualizationSystem;
  spatialIntegration: SpatialIndexingIntegration;
  accessibilityIntegration: AccessibilityIntegration;
  performanceValidator: PerformanceValidator;

  // Current state
  currentState: VisualFeedbackState;
  activeContext: VisualFeedbackContext | null;
  performanceMetrics: AnimationPerformanceMetrics;

  // Configuration
  isEnabled: boolean;
  configuration: VisualFeedbackConfig;

  // Methods
  setState: (
    state: VisualFeedbackState,
    context: VisualFeedbackContext,
    element?: HTMLElement,
  ) => Promise<void>;
  applyComponentFeedback: (
    componentType: ComponentType,
    state: VisualFeedbackState,
    context: VisualFeedbackContext,
    element: HTMLElement,
  ) => Promise<void>;
  handleError: (
    error: ErrorType,
    context: VisualFeedbackContext,
    element?: HTMLElement,
  ) => Promise<void>;
  updateConfiguration: (config: Partial<VisualFeedbackConfig>) => void;
  getPerformanceMetrics: () => AnimationPerformanceMetrics;
}

// Provider props
interface VisualFeedbackProviderProps {
  children: React.ReactNode;
  config?: Partial<VisualFeedbackConfig>;
  multiViewportSync?: Partial<MultiViewportSync>;
  spatialIndexingOptimization?: Partial<SpatialIndexingOptimization>;
  onPerformanceIssue?: (metrics: AnimationPerformanceMetrics) => void;
  onError?: (error: Error) => void;
  debugMode?: boolean;
}

// Default configuration
const defaultConfig: VisualFeedbackConfig = {
  state: VisualFeedbackState.EMPTY,
  intensity: "normal",
  duration: 300,
  easing: "ease-out",
  hardwareAccelerated: true,
  reducedMotion: false,
  multiModal: true,
};

// Context
const VisualFeedbackReactContext =
  createContext<VisualFeedbackContextValue | null>(null);

export const useVisualFeedback = () => {
  const context = useContext(VisualFeedbackReactContext);
  if (!context) {
    throw new Error(
      "useVisualFeedback must be used within a VisualFeedbackProvider",
    );
  }
  return context;
};

export const VisualFeedbackProvider: React.FC<VisualFeedbackProviderProps> = ({
  children,
  config = {},
  multiViewportSync = {},
  spatialIndexingOptimization = {},
  onPerformanceIssue,
  onError,
  debugMode = false,
}) => {
  // Merge configuration with defaults
  const finalConfig = useMemo(() => {
    // Only merge if config has actual properties
    if (Object.keys(config).length === 0) {
      return defaultConfig;
    }

    return {
      ...defaultConfig,
      ...config,
    };
  }, [JSON.stringify(config)]);

  // System initialization state
  const [isInitialized, setIsInitialized] = useState(false);
  const [initializationError, setInitializationError] = useState<Error | null>(
    null,
  );

  // Core system state
  const [currentState, setCurrentState] = useState<VisualFeedbackState>(
    VisualFeedbackState.EMPTY,
  );
  const [activeContext, setActiveContext] =
    useState<VisualFeedbackContext | null>(null);
  const [performanceMetrics, setPerformanceMetrics] =
    useState<AnimationPerformanceMetrics>({
      frameRate: 60,
      frameDropCount: 0,
      animationLatency: 0,
      memoryUsage: 0,
      gpuLayersActive: 0,
      reducedMotionActive: false,
    });

  // System instances
  const systemsRef = useRef<{
    feedbackSystem?: FourStateVisualFeedbackSystem;
    animationFramework?: HardwareAcceleratedAnimationFramework;
    componentPatterns?: ComponentSpecificFeedbackPatterns;
    performanceSystem?: PerformanceOptimizedAnimationSystem;
    errorSystem?: ErrorStateVisualizationSystem;
    spatialIntegration?: SpatialIndexingIntegration;
    accessibilityIntegration?: AccessibilityIntegration;
    performanceValidator?: PerformanceValidator;
  }>({});

  // Multi-viewport sync cleanup
  const syncCleanupRef = useRef<(() => void) | null | undefined>(null);

  // Broadcast channel for multi-viewport sync
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  // Store integration
  const { components, selectedComponents } = useEditorStore();
  const { activeViewport } = useResponsiveStore();

  // Multi-viewport synchronization configuration
  const multiViewportConfig = useMemo(() => {
    // Create a stable config object
    const defaultMultiViewportConfig = {
      enabled: true,
      synchronizedStates: [
        VisualFeedbackState.ACTIVE,
        VisualFeedbackState.SUCCESS,
      ],
      viewportMapping: new Map<string, string>(),
      delayCompensation: true,
      crossViewportAnimations: true,
    };

    // Only merge if multiViewportSync has actual properties
    if (Object.keys(multiViewportSync).length === 0) {
      return defaultMultiViewportConfig;
    }

    return {
      ...defaultMultiViewportConfig,
      ...multiViewportSync,
    };
  }, [JSON.stringify(multiViewportSync)]); // Use JSON.stringify for deep comparison

  // Spatial indexing optimization configuration
  const spatialOptimizationConfig = useMemo(() => {
    // Create a stable config object matching SpatialIndexingOptimization interface
    const defaultSpatialConfig: Partial<SpatialIndexingOptimization> = {
      enabled: true,
      cullingThreshold: 1000, // pixels
      viewportCulling: true,
      performanceScaling: true,
      lodSystem: true,
    };

    // Only merge if spatialIndexingOptimization has actual properties
    if (Object.keys(spatialIndexingOptimization).length === 0) {
      return defaultSpatialConfig;
    }

    return {
      ...defaultSpatialConfig,
      ...spatialIndexingOptimization,
    };
  }, [JSON.stringify(spatialIndexingOptimization)]); // Use JSON.stringify for deep comparison

  // Initialize visual feedback systems
  useEffect(() => {
    const initializeSystems = async () => {
      try {
        // Initialize animation framework first
        const animationFramework = new HardwareAcceleratedAnimationFramework({
          enabled: true,
          respectSystemPreferences: true,
          fallbackAnimations: new Map(),
          staticAlternatives: new Map(),
          minimumDuration: 50,
        });

        // Initialize four-state feedback system
        const feedbackSystem = new FourStateVisualFeedbackSystem(
          animationFramework,
        );

        // Initialize component-specific patterns
        const componentPatterns = new ComponentSpecificFeedbackPatterns(
          animationFramework,
        );

        // Create performance config for PerformanceOptimizedAnimationSystem
        const performanceConfig: Partial<PerformanceConfig> = {
          maxRenderTime: 16, // Target 60fps
          targetFrameRate: 60,
          memoryThreshold: 1000, // MB
          enableOptimizations: true,
          cullingEnabled: spatialOptimizationConfig.viewportCulling,
        };

        // Initialize performance optimization system
        const performanceSystem = new PerformanceOptimizedAnimationSystem(
          animationFramework,
          performanceConfig,
        );

        // Initialize error visualization system
        const errorSystem = new ErrorStateVisualizationSystem(
          animationFramework,
        );

        // Initialize spatial indexing integration
        const spatialIntegration = new SpatialIndexingIntegration(
          spatialOptimizationConfig,
        );

        // Initialize accessibility integration
        const accessibilityIntegration = new AccessibilityIntegration();

        // Initialize performance validator
        const performanceValidator = new PerformanceValidator({
          targetFrameRate: 60,
          measurementDuration: 3000,
          acceptableDropThreshold: 3,
          enableContinuousMonitoring: false, // Disabled to prevent infinite loops
          enablePerformanceReporting: debugMode,
        });

        // Setup performance validator callbacks
        performanceValidator.onPerformanceUpdate = (metrics) => {
          // Performance metrics update - logging disabled
        };

        performanceValidator.onTargetMissed = (result) => {
          console.warn("Performance target missed:", result);
          onPerformanceIssue?.(result as any);
        };

        // Store system references
        systemsRef.current = {
          feedbackSystem,
          animationFramework,
          componentPatterns,
          performanceSystem,
          errorSystem,
          spatialIntegration,
          accessibilityIntegration,
          performanceValidator,
        };

        // Performance monitoring disabled to prevent false warnings
        // setupPerformanceMonitoring(animationFramework);

        // Setup multi-viewport synchronization
        if (multiViewportConfig.enabled) {
          syncCleanupRef.current = setupMultiViewportSync() || null;
        }

        // Performance validation disabled to prevent RAF loops
        // Can be manually triggered if needed: performanceValidator.validateNow()

        setIsInitialized(true);
      } catch (error) {
        const err =
          error instanceof Error
            ? error
            : new Error("Failed to initialize visual feedback systems");
        setInitializationError(err);
        onError?.(err);
        console.error("Visual Feedback System initialization error:", error);
      }
    };

    initializeSystems();

    return () => {
      // Cleanup systems on unmount
      Object.values(systemsRef.current).forEach((system) => {
        if (system && "dispose" in system) {
          (system as any).dispose();
        }
      });

      // Cleanup multi-viewport sync if it exists
      if (syncCleanupRef.current) {
        syncCleanupRef.current();
        syncCleanupRef.current = null;
      }
    };
  }, [
    JSON.stringify(finalConfig),
    JSON.stringify(multiViewportConfig),
    JSON.stringify(spatialOptimizationConfig),
    debugMode,
    onError,
  ]);

  // Performance monitoring setup (disabled to prevent false warnings)
  const setupPerformanceMonitoring = useCallback(
    (animationFramework: HardwareAcceleratedAnimationFramework) => {
      // Disabled performance monitoring to prevent false 1fps warnings
      // The HardwareAcceleratedAnimationFramework metrics calculation appears to be incorrect

      // Set static baseline metrics instead
      setPerformanceMetrics({
        frameRate: 60,
        frameDropCount: 0,
        animationLatency: 16,
        memoryUsage: 0,
        gpuLayersActive: 0,
        reducedMotionActive: false,
      });

      // Return no-op cleanup function
      return () => {};
    },
    [],
  );

  // Multi-viewport synchronization setup
  const setupMultiViewportSync = useCallback(() => {
    if (!multiViewportConfig.enabled) return;

    // Create a broadcast channel for multi-viewport communication
    const syncChannel = new BroadcastChannel("terra-visual-feedback-sync");
    broadcastChannelRef.current = syncChannel;

    // Listen for sync messages from other viewports
    const handleSyncMessage = (event: MessageEvent) => {
      const { type, state, context, viewportId } = event.data;

      if (type === "visual-feedback-sync" && viewportId !== activeViewport) {
        // Apply synchronized state from another viewport
        if (
          multiViewportConfig.synchronizedStates.includes(state) &&
          systemsRef.current.feedbackSystem
        ) {
          const syncContext = {
            ...context,
            currentState: state,
            isSync: true, // Mark as sync to prevent infinite loops
          };

          // Apply with delay compensation if enabled
          const delay = multiViewportConfig.delayCompensation ? 16 : 0; // 1 frame delay

          setTimeout(() => {
            systemsRef.current.feedbackSystem?.setState(state, syncContext);
          }, delay);
        }
      }
    };

    syncChannel.addEventListener("message", handleSyncMessage);

    // Cleanup function
    return () => {
      syncChannel.removeEventListener("message", handleSyncMessage);
      syncChannel.close();
      broadcastChannelRef.current = null;
    };
  }, [multiViewportConfig, activeViewport]);

  // Function to broadcast state changes to other viewports
  const broadcastStateChange = useCallback(
    (state: VisualFeedbackState, context: VisualFeedbackContext) => {
      if (
        !multiViewportConfig.enabled ||
        !multiViewportConfig.synchronizedStates.includes(state) ||
        context.isSync ||
        !broadcastChannelRef.current
      ) {
        return;
      }

      broadcastChannelRef.current.postMessage({
        type: "visual-feedback-sync",
        state,
        context: {
          ...context,
          // Remove DOM references for serialization
          position: context.position,
          bounds: context.bounds,
          componentType: context.componentType,
          performanceLevel: context.performanceLevel,
          userPreferences: context.userPreferences,
        },
        viewportId: activeViewport,
        timestamp: performance.now(),
      });
    },
    [multiViewportConfig, activeViewport],
  );

  // Helper function to get state announcement messages
  const getStateAnnouncementMessage = useCallback(
    (state: VisualFeedbackState, context: VisualFeedbackContext): string => {
      switch (state) {
        case VisualFeedbackState.EMPTY:
          return "Canvas is empty. Use the AI chat to add components and start building.";
        case VisualFeedbackState.READY:
          return "Ready. Use AI chat to add components.";
        case VisualFeedbackState.ACTIVE:
          return `Positioning ${context.componentType || "component"}. Use arrow keys to adjust placement.`;
        case VisualFeedbackState.SUCCESS:
          return `${context.componentType || "Component"} successfully placed.`;
        case VisualFeedbackState.ERROR:
          return "Error occurred during placement. Check error details for recovery options.";
        default:
          return "";
      }
    },
    [],
  );

  // State management methods
  const setState = useCallback(
    async (
      state: VisualFeedbackState,
      context: VisualFeedbackContext,
      element?: HTMLElement,
    ): Promise<void> => {
      if (!isInitialized || !systemsRef.current.feedbackSystem) return;

      try {
        // Optimize context with spatial awareness
        let optimizedContext = context;
        if (
          spatialOptimizationConfig.enabled &&
          systemsRef.current.spatialIntegration
        ) {
          optimizedContext =
            systemsRef.current.spatialIntegration.optimizeContext(context);

          // Register component with spatial index if element provided
          if (element && context.componentType) {
            const elementRect = element.getBoundingClientRect();
            systemsRef.current.spatialIntegration.registerComponent(
              element.id || `component-${Date.now()}`,
              {
                x: elementRect.left,
                y: elementRect.top,
                width: elementRect.width,
                height: elementRect.height,
              },
              context.componentType,
              context.position.z || 0,
            );
          }
        }

        // Apply accessibility optimizations
        if (systemsRef.current.accessibilityIntegration) {
          optimizedContext =
            systemsRef.current.accessibilityIntegration.optimizeForAccessibility(
              optimizedContext,
            );

          // Apply accessibility adaptations to element
          if (element) {
            systemsRef.current.accessibilityIntegration.setAriaAttributes(
              element,
              state,
              optimizedContext,
            );
            systemsRef.current.accessibilityIntegration.applyReducedMotionAdaptations(
              element,
              state,
            );
            systemsRef.current.accessibilityIntegration.applyHighContrastAdaptations(
              element,
            );

            // Announce state changes to screen reader
            const stateMessage = getStateAnnouncementMessage(
              state,
              optimizedContext,
            );
            if (stateMessage) {
              systemsRef.current.accessibilityIntegration.announceToScreenReader(
                stateMessage,
                state === VisualFeedbackState.ERROR ? "assertive" : "polite",
              );
            }
          }
        }

        setCurrentState(state);
        setActiveContext(optimizedContext);

        // Apply four-state feedback (skip for canvas elements to prevent highlighting)
        if (
          element &&
          !element.hasAttribute("data-no-visual-feedback")
        ) {
          await systemsRef.current.feedbackSystem.setState(
            state,
            optimizedContext,
            element,
          );
        }

        // Apply component-specific feedback if component type is specified (skip for canvas elements)
        if (
          optimizedContext.componentType &&
          element &&
          systemsRef.current.componentPatterns &&
          !element.hasAttribute("data-no-visual-feedback")
        ) {
          await systemsRef.current.componentPatterns.applyComponentFeedback(
            optimizedContext.componentType,
            state,
            optimizedContext,
            element,
          );
        }

        // Handle spatial indexing optimizations
        if (
          spatialOptimizationConfig.enabled &&
          systemsRef.current.performanceSystem
        ) {
          await systemsRef.current.performanceSystem.optimizeForSpatialContext(
            optimizedContext,
            element,
          );
        }

        // Broadcast state change to other viewports
        broadcastStateChange(state, optimizedContext);
      } catch (error) {
        const err =
          error instanceof Error
            ? error
            : new Error("Failed to set visual feedback state");
        console.error("setState error:", err);
        onError?.(err);
      }
    },
    [
      isInitialized,
      spatialOptimizationConfig,
      debugMode,
      onError,
      broadcastStateChange,
      getStateAnnouncementMessage,
    ],
  );

  // Component-specific feedback application
  const applyComponentFeedback = useCallback(
    async (
      componentType: ComponentType,
      state: VisualFeedbackState,
      context: VisualFeedbackContext,
      element: HTMLElement,
    ): Promise<void> => {
      if (!isInitialized || !systemsRef.current.componentPatterns) return;

      try {
        await systemsRef.current.componentPatterns.applyComponentFeedback(
          componentType,
          state,
          context,
          element,
        );
      } catch (error) {
        const err =
          error instanceof Error
            ? error
            : new Error("Failed to apply component feedback");
        console.error("applyComponentFeedback error:", err);
        onError?.(err);
      }
    },
    [isInitialized, onError],
  );

  // Error handling
  const handleError = useCallback(
    async (
      error: ErrorType,
      context: VisualFeedbackContext,
      element?: HTMLElement,
    ): Promise<void> => {
      if (!isInitialized || !systemsRef.current.errorSystem) return;

      try {
        await systemsRef.current.errorSystem.visualizeError(
          error,
          context,
          element,
        );
        setCurrentState(VisualFeedbackState.ERROR);
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error("Failed to handle visual feedback error");
        console.error("handleError error:", error);
        onError?.(error);
      }
    },
    [isInitialized, onError],
  );

  // Configuration updates
  const updateConfiguration = useCallback(
    (newConfig: Partial<VisualFeedbackConfig>) => {
      // Update configuration would be implemented here
      // This would involve updating the systems with new configuration
    },
    [],
  );

  // Performance metrics getter
  const getPerformanceMetrics = useCallback((): AnimationPerformanceMetrics => {
    return { ...performanceMetrics };
  }, [performanceMetrics]);

  // AI-first design - no drag state synchronization needed
  useEffect(() => {
    if (!isInitialized) return;

    // Set initial state based on component count
    const targetState =
      components.length === 0
        ? VisualFeedbackState.EMPTY
        : VisualFeedbackState.READY;

    // Only update if state actually changed
    if (targetState === currentState) return;

    // Create default context
    const context: VisualFeedbackContext = {
      currentState: targetState,
      componentType: ComponentType.CONTAINER,
      position: { x: 0, y: 0, z: 0 },
      bounds: { x: 0, y: 0, width: 200, height: 100 },
      isVisible: true,
      performanceLevel: "high",
      userPreferences: {
        reducedMotion: false,
        highContrast: false,
        touchDevice: "ontouchstart" in window,
      },
    };

    setState(targetState, context);
  }, [
    isInitialized,
    components.length,
    currentState,
    setState,
  ]);

  // Context value
  const contextValue = useMemo<VisualFeedbackContextValue>(
    () => ({
      feedbackSystem: systemsRef.current.feedbackSystem!,
      animationFramework: systemsRef.current.animationFramework!,
      componentPatterns: systemsRef.current.componentPatterns!,
      performanceSystem: systemsRef.current.performanceSystem!,
      errorSystem: systemsRef.current.errorSystem!,
      spatialIntegration: systemsRef.current.spatialIntegration!,
      accessibilityIntegration: systemsRef.current.accessibilityIntegration!,
      performanceValidator: systemsRef.current.performanceValidator!,
      currentState,
      activeContext,
      performanceMetrics,
      isEnabled: isInitialized && !initializationError,
      configuration: finalConfig,
      setState,
      applyComponentFeedback,
      handleError,
      updateConfiguration,
      getPerformanceMetrics,
    }),
    [
      currentState,
      activeContext,
      performanceMetrics,
      isInitialized,
      initializationError,
      finalConfig,
      setState,
      applyComponentFeedback,
      handleError,
      updateConfiguration,
      getPerformanceMetrics,
    ],
  );

  // Show error state if initialization failed
  if (initializationError) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="font-semibold text-red-800">
          Visual Feedback System Error
        </h3>
        <p className="text-red-600">{initializationError.message}</p>
        <p className="text-sm text-red-500 mt-2">
          Visual feedback will be disabled. Basic functionality will continue to
          work.
        </p>
      </div>
    );
  }

  // Show loading state during initialization
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2 text-sm text-muted-foreground">
          Initializing visual feedback system...
        </span>
      </div>
    );
  }

  return (
    <VisualFeedbackReactContext.Provider value={contextValue}>
      {children}
    </VisualFeedbackReactContext.Provider>
  );
};

export default VisualFeedbackProvider;
